import { Button, Divider, SelectItem, TmdxSelect, TmdxTextarea } from '@ds';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { TrialAnswer, TrialAnsweredQuestionModel, TrialQuestion } from '@/api/apis/case';
import { Uuid } from '@/api/types';
import { useUpdateCaseMutation } from '@/queries/case';
import { HEART_COLD_STORAGE_OPTIONS, LUNGS_COLD_STORAGE_OPTIONS } from '@/constants/trial.constants';
import SectionRow from '../SectionRow';
import { retrieveUnifiedError } from '@/api/utils';

type EditSCSMethodProps = {
  caseId: Uuid;
  storageQuestion:
    | TrialQuestion.EnhanceColdStaticPreservationConfirmation
    | TrialQuestion.DenovoColdStaticPreservationConfirmation;
  questionnaire: TrialAnsweredQuestionModel[];
  disabled: boolean;
  onClose: () => void;
};

const SCS_METHOD_MAX_LENGTH = 64;

export const EditSCSMethod = ({ caseId, storageQuestion, questionnaire, disabled, onClose }: EditSCSMethodProps) => {
  const isEnhance = storageQuestion === TrialQuestion.EnhanceColdStaticPreservationConfirmation;
  const selectedScsQA = questionnaire.find((q) => q.question === storageQuestion);
  const [scsMethod, setScsMethod] = useState(selectedScsQA?.answer ?? TrialAnswer.Other);
  const [customAnswer, setCustomAnswer] = useState(selectedScsQA?.customAnswer || '');
  const { mutateAsync: updateCase, isPending } = useUpdateCaseMutation();

  const getUpdatedQuestionnaire = (newAnswer: TrialAnswer, newCustomAnswer: string) => [
    ...questionnaire.filter((q) => q.question !== storageQuestion),
    { question: storageQuestion, answer: newAnswer, customAnswer: newCustomAnswer },
  ];

  const updateCaseWithToast = async (newQuestionnaire: TrialAnsweredQuestionModel[]) => {
    try {
      await updateCase({
        caseId,
        questionnaire: newQuestionnaire,
      });
      toast.success('Static cold storage method updated');
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    } finally {
      onClose();
    }
  };

  const handleMethodSave = async () => {
    const newCustomAnswer = scsMethod === TrialAnswer.Other ? customAnswer : '';
    const newQuestionnaire = getUpdatedQuestionnaire(scsMethod, newCustomAnswer);
    await updateCaseWithToast(newQuestionnaire);
  };

  const scsMethods = isEnhance ? HEART_COLD_STORAGE_OPTIONS : LUNGS_COLD_STORAGE_OPTIONS;

  return (
    <div className="flex flex-col gap-4">
      <SectionRow label="Preferred static cold storage method" truncate={false} centerItems={false}>
        <div className="flex flex-col gap-4">
          <TmdxSelect
            selectionMode="single"
            isDisabled={disabled}
            placeholder="Select preferred SCS method"
            aria-label="Select preferred SCS method"
            selectedKeys={scsMethod ? [scsMethod] : []}
            disallowEmptySelection
            onSelectionChange={(keys) => {
              const selectedValue = Array.from(keys)[0];
              setScsMethod(selectedValue as TrialAnswer);
            }}
          >
            {scsMethods.map((option) => (
              <SelectItem key={option.value}>{option.name}</SelectItem>
            ))}
          </TmdxSelect>
          {scsMethod === TrialAnswer.Other && (
            <TmdxTextarea
              isDisabled={disabled}
              fullWidth
              disableAnimation
              minRows={3}
              maxRows={3}
              maxLength={SCS_METHOD_MAX_LENGTH}
              placeholder="Specify other option"
              value={customAnswer}
              onChange={(e) => setCustomAnswer(e.target.value)}
            />
          )}
        </div>
      </SectionRow>
      {!disabled && (
        <>
          <Divider />
          <div className="flex gap-4 justify-end">
            <Button isDisabled={isPending} variant="light" size="sm" onClick={onClose}>
              Discard
            </Button>
            <Button
              isLoading={isPending}
              isDisabled={customAnswer.length > SCS_METHOD_MAX_LENGTH}
              variant="solid"
              size="sm"
              onClick={handleMethodSave}
            >
              Save Changes
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
